import compiled from "@brand";
import { makeCopy, mergeBrandConfig, type Brand, type BrandConfig, type BrandConfigInput, type CopyKey } from "@cfg/brand-sdk";
import { applyTheme } from "@cfg/theme";

/**
 * The brand is compiled in through the `@brand` alias (code: scenes, slots,
 * widgets) and then overlaid with `/brand.json` (data: tokens, copy, feature
 * flags, endpoints) that BrandSiteStack writes at deploy. Dev without a
 * brand.json simply uses the compiled config.
 */
export async function resolveBrand(): Promise<Brand> {
  let overrides: Partial<BrandConfigInput> | null = null;
  try {
    const res = await fetch("/brand.json", { cache: "no-cache" });
    if (res.ok) overrides = (await res.json()) as Partial<BrandConfigInput>;
  } catch {
    // no brand.json in dev
  }
  const env = import.meta.env;
  const envOverrides: Partial<BrandConfigInput> = {};
  if (env.VITE_API_URL) envOverrides.apiBaseUrl = env.VITE_API_URL;
  if (env.VITE_AUTH_AUTHORITY && env.VITE_AUTH_CLIENT_ID) {
    envOverrides.auth = { authority: env.VITE_AUTH_AUTHORITY, clientId: env.VITE_AUTH_CLIENT_ID, domain: env.VITE_AUTH_DOMAIN };
  }
  const config = mergeBrandConfig(mergeBrandConfig(compiled.config, overrides), envOverrides);
  const brand: Brand = { ...compiled, config };
  applyTheme(config.theme);
  if (brand.css) {
    let el = document.getElementById("cfg-brand-css") as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement("style");
      el.id = "cfg-brand-css";
      document.head.appendChild(el);
    }
    el.textContent = brand.css;
  }
  document.title = config.name;
  return brand;
}

export type Copy = ReturnType<typeof makeCopy>;
export { makeCopy };

/**
 * Copy for a key that replaced an older one: the first key the brand
 * overrides wins, else the default of the first key. Keeps brands that set
 * the legacy key (e.g. `community.search`) reading the same way.
 */
export function copyFirst(config: Pick<BrandConfig, "copy">, copy: Copy, keys: [CopyKey, ...CopyKey[]], vars?: Record<string, string | number>): string {
  const overridden = keys.find((k) => config.copy[k] !== undefined);
  return copy(overridden ?? keys[0], vars);
}
